/**
 * useAutoRefresh — Sprint 4.2A.
 *
 * Hook que executa `callback` em intervalo regular, com pause opcional.
 *
 * Decisões:
 *   - Polling, não WS (briefing veta alterar backend).
 *   - Quando `paused=true`, o intervalo NÃO dispara — mas o setInterval
 *     continua agendado. Isso significa que retomar a partir do pause
 *     espera no máximo `intervalMs` para o próximo tick (sem refresh
 *     imediato — evita disparos abruptos quando o usuário acaba de
 *     terminar um drag/lasso).
 *   - lastUpdate é Date | null. null = ainda não rodou (primeira carga
 *     normalmente vem do useEffect inicial do consumidor, não do hook).
 *   - Erros do callback NÃO atualizam lastUpdate (assim a UI mostra
 *     visivelmente que algo travou).
 */
import { useEffect, useRef, useState } from "react";

export function useAutoRefresh({ intervalMs = 20000, paused = false, callback }) {
  const [lastUpdate, setLastUpdate] = useState(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (intervalMs <= 0) return undefined;
    const id = setInterval(async () => {
      if (paused) return;
      try {
        const fn = callbackRef.current;
        if (fn) {
          await fn();
          setLastUpdate(new Date());
        }
      } catch {
        // Não atualiza lastUpdate em erro — sinal visual de que pausou.
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, paused]);

  return { lastUpdate };
}
